import React, { useEffect, useState } from 'react';
import { Loader, X, ShieldCheck } from 'lucide-react';
import {
  getSessionLogDetails,
  type SessionAnalysis,
  type SessionLogDetails,
} from '../../services/api/sessions';
import { SessionAdvancedDetails } from './SessionAdvancedDetails';
import { SessionContextBar } from './SessionContextBar';
import { SessionTimeline } from './SessionTimeline';
import { SessionAnalystPanel } from './SessionAnalystPanel';
import { SessionVerdictPanel } from './SessionVerdictPanel';
import { FlagButton } from './FlagButton';
import { isUuid } from './uuid';
import { EraseChatImagesButton } from './EraseChatImagesButton';
import { TicketFromSessionButton } from './TicketFromSessionButton';

interface SessionLogViewerProps {
  sessionId: string;
  onClose?: () => void;
}

export const SessionLogViewer: React.FC<SessionLogViewerProps> = ({ sessionId, onClose }) => {
  const [sessionDetails, setSessionDetails] = useState<SessionLogDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getSessionLogDetails(sessionId);
        if (!cancelled) setSessionDetails(data);
      } catch (err) {
        if (!cancelled)
          setError(
            `Failed to load session: ${err instanceof Error ? err.message : 'Unknown error'}`,
          );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-slate-200 shadow-lg">
      <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200">
        <div>
          <h3 className="font-semibold text-slate-900">Sessionsdetaljer</h3>
          <p className="text-xs text-slate-500 mt-1 font-mono">{sessionId}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="p-4 text-red-600">{error}</div>
        ) : sessionDetails ? (
          <SessionContent details={sessionDetails} />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-500">
            No session details found
          </div>
        )}
      </div>
    </div>
  );
};

const SessionContent = ({ details }: { details: SessionLogDetails }) => {
  const { state, totals } = details;
  const outfitSelections = Object.values(state.outfitState?.selections ?? []);

  const canFlag = isUuid(details.sessionId);
  const lastAssistant = [...details.timeline].reverse().find((e) => e.role === 'assistant');
  const lastUser = [...details.timeline].reverse().find((e) => e.role === 'user');

  return (
    <div className="space-y-4 p-4">
      <TicketFromSessionButton sessionId={details.sessionId} />
      <EraseChatImagesButton sessionId={details.sessionId} />

      {canFlag && lastAssistant ? (
        <FlagButton
          sessionId={details.sessionId}
          userMessage={lastUser?.content ?? ''}
          assistantAnswer={lastAssistant.content}
          triggerLabel="Markera sessionen som dålig"
        />
      ) : !canFlag ? (
        <div className="text-xs text-slate-400">Flaggning stöds ännu inte för den här kanalen.</div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-4">
        {[
          { label: 'Kanal', value: state.channel || 'website' },
          { label: 'Status', value: state.status || 'active' },
          { label: 'Meddelanden', value: totals.messageCount },
          {
            label: 'Snittsvar',
            value: totals.averageResponseMs ? `${totals.averageResponseMs} ms` : 'N/A',
          },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
          </div>
        ))}
      </div>

      <SessionContextBar details={details} />

      <div className="flex flex-wrap gap-2 text-xs">
        {/* Inloggade kunder får en verifierad currentOrder utan ordernummer —
            det är kontot som är verifierat, inte någon order. */}
        {state.currentOrder?.isVerified && state.currentOrder?.orderNumber && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-green-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Verifierad order
          </span>
        )}
        {state.currentOrder?.isVerified && !state.currentOrder?.orderNumber && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-slate-600">
            Inloggad kund
          </span>
        )}
        {state.outfitState?.phase && (
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-3 py-1 text-orange-700">
            Outfit: {state.outfitState.phase}
          </span>
        )}
        {state.outfitState?.occasionLabel && (
          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-purple-700">
            Tillfälle: {state.outfitState.occasionLabel}
          </span>
        )}
        {state.currentOrder?.orderNumber && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-blue-700">
            Order: {state.currentOrder.orderNumber}
          </span>
        )}
      </div>

      <SessionAnalystPanel
        sessionId={details.sessionId}
        timelineLength={details.timeline.length}
        cachedAnalysis={(state.metadata as any)?.analysis as SessionAnalysis | undefined}
      />

      <SessionVerdictPanel sessionId={details.sessionId} />

      <SessionAdvancedDetails sessionDetails={details} />

      <SessionTimeline
        sessionId={details.sessionId}
        canFlag={canFlag}
        timeline={details.timeline}
        outfitSelections={outfitSelections}
        totalPrice={state.outfitState?.totalPrice}
        currency={state.outfitState?.currency}
        language={state.uiLanguage ?? state.language ?? 'sv'}
      />
    </div>
  );
};
